import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type {
	AddFacebookTemplateDto,
	EditFacebookTemplateDto,
} from '@adflow/shared/data-facebook-ads';
import {
	AddFacebookTemplate,
	ArchiveFacebookTemplate,
	DeleteFacebookTemplate,
	DeleteFacebookTemplates,
	GetFacebookTemplateAdAccountCustomAudiences,
	GetFacebookTemplateAdAccountPages,
	GetFacebookTemplateAdAccounts,
	GetFacebookTemplateAdAccountSavedAudiences,
	GetFacebookTemplateAdInterests,
	GetFacebookTemplateBehaviors,
	GetFacebookTemplateCities,
	GetFacebookTemplateCountries,
	GetFacebookTemplateEnums,
	GetFacebookTemplateProductCatalogs,
	GetFacebookTemplateProductSets,
	GetFacebookTemplateRegions,
	GetFacebookTemplateUploadProgress,
	GetFacebookTemplateZips,
	UploadFacebookTemplate,
} from '@adflow/shared/data-facebook-ads';
import type {
	ListFacebookTemplatesHistoryQuery,
	ListFacebookTemplatesQuery,
} from '@adflow/shared/util-history';
import {
	EditFacebookTemplate,
	GetFacebookTemplate,
	GetFacebookTemplateHistory,
	ListFacebookTemplatesWithUploadResult,
} from '@adflow/shared/util-history';

export const facebookTemplateKeys = {
	all: ['facebook', 'template'] as const,
	allList: () => [...facebookTemplateKeys.all, 'list'] as const,
	list: (query: ListFacebookTemplatesQuery) =>
		[...facebookTemplateKeys.allList(), query] as const,
	view: (templateId: number) =>
		[...facebookTemplateKeys.all, 'view', templateId] as const,
	enums: () => [...facebookTemplateKeys.all, 'enums'] as const,
	adInterests: (search: string) =>
		[...facebookTemplateKeys.all, 'adInterests', search] as const,
	zips: (search: string) =>
		[...facebookTemplateKeys.all, 'zips', search] as const,
	regions: (search: string) =>
		[...facebookTemplateKeys.all, 'regions', search] as const,
	cities: (search: string) =>
		[...facebookTemplateKeys.all, 'cities', search] as const,
	countries: (search: string) =>
		[...facebookTemplateKeys.all, 'countries', search] as const,
	behaviors: () => [...facebookTemplateKeys.all, 'behaviors'] as const,
	adAccounts: () => [...facebookTemplateKeys.all, 'adAccounts'] as const,
	adAccountSavedAudiences: (avbId: string) =>
		[...facebookTemplateKeys.all, 'adAccountSavedAudiences', avbId] as const,
	adAccountPages: (avbId: string, search: string) =>
		[...facebookTemplateKeys.all, 'adAccountPages', avbId, search] as const,
	adAccountCustomAudiences: (avbId: string) =>
		[...facebookTemplateKeys.all, 'adAccountCustomAudiences', avbId] as const,
	productCatalogs: (avbIds: string[]) =>
		[...facebookTemplateKeys.all, 'productCatalogs', avbIds] as const,
	productSets: (avbId: string, productCatalogId: string) =>
		[
			...facebookTemplateKeys.all,
			'productSets',
			avbId,
			productCatalogId,
		] as const,
	history: (templateId: number, query?: ListFacebookTemplatesHistoryQuery) =>
		[...facebookTemplateKeys.all, 'history', templateId, query] as const,
	uploadTemplate: (templateId: number) =>
		[...facebookTemplateKeys.all, 'uploadTemplate', templateId] as const,
	uploadProgress: (templateId: number) =>
		[...facebookTemplateKeys.all, 'uploadProgress', templateId] as const,
};

export function useListFacebookTemplatesQuery(
	query: ListFacebookTemplatesQuery,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.list(query),
		queryFn: ({ signal }) =>
			apiRequest(ListFacebookTemplatesWithUploadResult, { query }, { signal }),
		enabled,
	});
}

export const fetchFacebookTemplate = async (templateId: number) => {
	return apiRequest(GetFacebookTemplate, { params: { templateId } });
};

export function useFacebookTemplateQuery(templateId: number) {
	return useQuery({
		queryKey: facebookTemplateKeys.view(templateId),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplate, { params: { templateId } }, { signal }),
	});
}

export function useAddFacebookTemplateMutation(duplicated?: boolean) {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['facebook', 'common']);
	return useMutation({
		mutationFn: (body: AddFacebookTemplateDto) =>
			apiRequest(AddFacebookTemplate, { body, query: { duplicated } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				facebookTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
			if (duplicated) {
				createSuccessToast(t('common:templateDuplicatedSuccessfully'));
			} else {
				createSuccessToast(t('facebook:success.savingTemplate'));
			}
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('facebook:error.savingTemplate'),
			});
		},
	});
}

export function useUploadFacebookTemplateMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('facebook');
	return useMutation({
		mutationFn: ({ templateId }: { templateId: number }) =>
			apiRequest(UploadFacebookTemplate, { params: { templateId } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				facebookTemplateKeys.uploadTemplate(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
			createSuccessToast(t('facebookAdsUpload.begin'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('facebookAdsUpload.error'),
			});
		},
	});
}

export function useFacebookTemplateUploadProgressQuery(
	templateId: number,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.uploadProgress(templateId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateUploadProgress,
				{ params: { templateId } },
				{ signal }
			),
		enabled,
	});
}

export function useFacebookTemplateProductCatalogs(avbIds: string[]) {
	return useQuery({
		queryKey: facebookTemplateKeys.productCatalogs(avbIds),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateProductCatalogs,
				{ query: { avbIds } },
				{ signal }
			),
		enabled: (avbIds?.length ?? 0) > 0,
	});
}

export function useFacebookTemplateProductSets(
	avbId: string,
	productCatalogId: string
) {
	return useQuery({
		queryKey: facebookTemplateKeys.productSets(avbId, productCatalogId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateProductSets,
				{ query: { avbId, productCatalogId } },
				{ signal }
			),
		enabled: Boolean(avbId) && Boolean(productCatalogId),
	});
}

export function useEditFacebookTemplateMutation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('facebook');
	return useMutation({
		mutationFn: ({
			templateId,
			edits,
		}: {
			templateId: number;
			edits: EditFacebookTemplateDto;
		}) =>
			apiRequest(EditFacebookTemplate, { params: { templateId }, body: edits }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				facebookTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
			createSuccessToast(t('success.savingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.savingTemplate'),
			});
		},
	});
}

export function useArchiveFacebookTemplateMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (templateId: number) =>
			apiRequest(ArchiveFacebookTemplate, { params: { templateId } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				facebookTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
		},
	});
}

export function useDeleteFacebookTemplateMutation() {
	const { t } = useTranslation('facebook');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateId: number) =>
			apiRequest(DeleteFacebookTemplate, { params: { templateId } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
			createSuccessToast(t('success.deletingTemplate', { count: 1 }));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.deletingTemplate'),
			});
		},
	});
}

export function useDeleteFacebookTemplatesMutation() {
	const { t } = useTranslation('facebook');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateIds: number[]) =>
			apiRequest(DeleteFacebookTemplates, { body: { templateIds } }),
		onSuccess: (_data, templateIds) => {
			void queryClient.invalidateQueries({
				queryKey: facebookTemplateKeys.allList(),
			});
			createSuccessToast(
				t('success.deletingTemplate', { count: templateIds.length })
			);
		},
		onError: (err, templateIds) => {
			handleRequestErrors([err], {
				errorMessage: t('error.deletingTemplate', {
					count: templateIds.length,
				}),
			});
		},
	});
}

export function useFacebookEnums() {
	return useQuery({
		queryKey: facebookTemplateKeys.enums(),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateEnums, {}, { signal }),
	});
}

export function useFacebookAccounts() {
	return useQuery({
		queryKey: facebookTemplateKeys.adAccounts(),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateAdAccounts, {}, { signal }),
	});
}

export function useFacebookAccountSavedAudiences(
	avbId: string,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.adAccountSavedAudiences(avbId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateAdAccountSavedAudiences,
				{ params: { avbId } },
				{ signal }
			),
		enabled: !!avbId && enabled,
	});
}

export function useFacebookAccountPages(
	avbId: string,
	search: string,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.adAccountPages(avbId, search),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateAdAccountPages,
				{ params: { avbId }, query: { search } },
				{ signal }
			),
		enabled: !!avbId && enabled,
	});
}

export function useFacebookAccountCustomAudiences(
	avbId: string,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.adAccountCustomAudiences(avbId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateAdAccountCustomAudiences,
				{ params: { avbId } },
				{ signal }
			),
		enabled: !!avbId && enabled,
	});
}

export function useFacebookAdInterests(search: string, enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.adInterests(search),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateAdInterests,
				{ query: { search } },
				{ signal }
			),
		enabled,
	});
}

export function useFacebookZips(search: string, enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.zips(search),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateZips, { query: { search } }, { signal }),
		enabled,
	});
}

export function useFacebookRegions(search: string, enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.regions(search),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateRegions, { query: { search } }, { signal }),
		enabled,
	});
}

export function useFacebookCities(search: string, enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.cities(search),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateCities, { query: { search } }, { signal }),
		enabled,
	});
}

export function useFacebookCountries(search: string, enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.countries(search),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateCountries,
				{ query: { search } },
				{ signal }
			),
		enabled,
	});
}

export function useFacebookBehaviors(enabled = true) {
	return useQuery({
		queryKey: facebookTemplateKeys.behaviors(),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookTemplateBehaviors, {}, { signal }),
		enabled,
	});
}

export function useFacebookTemplateHistoryQuery(
	templateId: number,
	query: ListFacebookTemplatesHistoryQuery,
	enabled = true
) {
	return useQuery({
		queryKey: facebookTemplateKeys.history(templateId, query),
		queryFn: ({ signal }) =>
			apiRequest(
				GetFacebookTemplateHistory,
				{
					params: { templateId },
					query,
				},
				{ signal }
			),
		enabled,
	});
}
